import React, { useMemo, useRef, useState } from 'react';
import { CHART_GRID, CHART_TICK } from 'src/styles/chartTheme';
import { useTranslation } from 'react-i18next';
import { Chart } from 'react-chartjs-2';
import type { Chart as ChartJS, ChartData, ChartOptions, ChartType, Plugin } from 'chart.js';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useTheme } from 'src/providers/ThemeProvider';
import { localeNumber } from 'src/localization/locale';
import { AiChatChart, saveBlob } from 'src/services/aiChat';
import { SERIES_DARK, SERIES_LIGHT, SURFACE_DARK, SURFACE_LIGHT, withAlpha } from './chartPalette';
import styles from './AiChat.module.scss';

const AXIS_TEXT = CHART_TICK;

const chartJsType = (type: AiChatChart['type']): ChartType => {
  switch (type) {
    case 'line':
    case 'area':
      return 'line';
    case 'pie':
      return 'pie';
    case 'doughnut':
      return 'doughnut';
    default:
      return 'bar';
  }
};

const slug = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'chart';

const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png';
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const ChartCard: React.FC<{ chart: AiChatChart }> = ({ chart }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const chartRef = useRef<ChartJS | null>(null);
  const [showTable, setShowTable] = useState(false);

  const dark = theme === 'dark';
  const palette = dark ? SERIES_DARK : SERIES_LIGHT;
  const surface = dark ? SURFACE_DARK : SURFACE_LIGHT;
  const gridColor = dark ? CHART_GRID.dark : CHART_GRID.light;
  const isPie = chart.type === 'pie' || chart.type === 'doughnut';
  const isLine = chart.type === 'line' || chart.type === 'area';
  const type = chartJsType(chart.type);

  const formatValue = (value: number): string => {
    if (chart.value_format === 'currency' && chart.currency) {
      return localeNumber(value, { style: 'currency', currency: chart.currency, maximumFractionDigits: 2 });
    }
    if (chart.value_format === 'percent') return `${localeNumber(value, { maximumFractionDigits: 1 })} %`;
    return localeNumber(value, { maximumFractionDigits: 2 });
  };

  const data = useMemo<ChartData>(() => {
    if (isPie) {
      const series = chart.datasets[0];
      return {
        labels: chart.labels,
        datasets: [
          {
            label: series.label,
            data: series.data.map(v => v ?? 0),
            backgroundColor: chart.labels.map((_, i) => palette[i % palette.length]),
            borderColor: surface,
            borderWidth: 2,
            hoverOffset: 4,
          },
        ],
      };
    }
    return {
      labels: chart.labels,
      datasets: chart.datasets.map((series, i) => {
        const color = palette[i % palette.length];
        if (isLine) {
          return {
            label: series.label,
            data: series.data,
            borderColor: color,
            backgroundColor: chart.type === 'area' ? withAlpha(color, 0.12) : color,
            fill: chart.type === 'area',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: color,
            pointHoverBorderColor: surface,
            pointHoverBorderWidth: 2,
            tension: 0.25,
            spanGaps: true,
          };
        }
        return {
          label: series.label,
          data: series.data,
          backgroundColor: color,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: 'start' as const,
          maxBarThickness: 24,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        };
      }),
    };
  }, [chart, palette, surface, isPie, isLine]);

  const options = useMemo<ChartOptions>(() => {
    const stacked = chart.type === 'stacked_bar';
    const base: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      plugins: {
        legend: {
          display: isPie || chart.datasets.length > 1,
          position: 'bottom',
          labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, color: AXIS_TEXT, padding: 14 },
        },
        tooltip: {
          mode: isPie ? 'nearest' : 'index',
          intersect: false,
          callbacks: {
            label: ctx => {
              const raw = typeof ctx.raw === 'number' ? ctx.raw : Number(ctx.raw ?? 0);
              const name = isPie ? String(ctx.label ?? '') : ctx.dataset.label ?? '';
              return `${name}: ${formatValue(raw)}`;
            },
          },
        },
      },
      interaction: { mode: isPie ? 'nearest' : 'index', intersect: false },
    };
    if (isPie) return base;
    const horizontal = chart.type === 'horizontal_bar';
    const categoryAxis = {
      stacked,
      grid: { display: false },
      border: { display: false },
      ticks: { color: AXIS_TEXT, autoSkip: true, maxRotation: 0, padding: 6 },
      title: { display: !!chart.x_label, text: chart.x_label, color: AXIS_TEXT },
    };
    const valueAxis = {
      stacked,
      beginAtZero: true,
      grid: { color: gridColor, drawTicks: false },
      border: { display: false },
      ticks: { color: AXIS_TEXT, padding: 8, callback: (value: string | number) => formatValue(Number(value)) },
      title: { display: !!chart.y_label, text: chart.y_label, color: AXIS_TEXT },
    };
    return {
      ...base,
      indexAxis: horizontal ? 'y' : 'x',
      scales: horizontal ? { x: valueAxis, y: categoryAxis } : { x: categoryAxis, y: valueAxis },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, gridColor, isPie]);

  const plugins = useMemo<Plugin[]>(
    () => [
      {
        id: 'aiChatSurface',
        beforeDraw: instance => {
          const { ctx, width, height } = instance;
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = surface;
          ctx.fillRect(0, 0, width, height);
          ctx.restore();
        },
      },
    ],
    [surface],
  );

  const download = () => {
    const instance = chartRef.current;
    if (!instance) return;
    saveBlob(dataUrlToBlob(instance.toBase64Image('image/png', 1)), `${slug(chart.title)}.png`);
  };

  return (
    <div className="self-start w-full max-w-[760px] flex flex-col gap-8 rounded-base border-subtle surface-elevated p-12">
      <div className="flex items-center justify-between gap-12">
        <span className="typo-text-14--medium text-full truncate">{chart.title}</span>
        <div className="flex items-center gap-4 shrink-0">
          <Button
            tone={ButtonToneProps.GHOST}
            icon={showTable ? 'bar-chart' : 'note'}
            ariaLabel={showTable ? t('tracking.ai_chat.chart_show_chart') : t('tracking.ai_chat.chart_show_table')}
            hoverText={showTable ? t('tracking.ai_chat.chart_show_chart') : t('tracking.ai_chat.chart_show_table')}
            onClick={() => setShowTable(!showTable)}
          />
          {!showTable && (
            <Button
              tone={ButtonToneProps.GHOST}
              icon="download"
              ariaLabel={t('tracking.ai_chat.chart_download')}
              hoverText={t('tracking.ai_chat.chart_download')}
              onClick={download}
            />
          )}
        </div>
      </div>
      {showTable ? (
        <div className={styles.markdown}>
          <table>
            <thead>
              <tr>
                <th>{chart.x_label || ''}</th>
                {chart.datasets.map(series => (
                  <th key={series.label}>{series.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart.labels.map((label, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <td>{label}</td>
                  {chart.datasets.map(series => (
                    <td key={series.label}>{series.data[i] == null ? '—' : formatValue(series.data[i] as number)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.chartCanvas}>
          <Chart ref={chartRef} type={type} data={data} options={options} plugins={plugins} />
        </div>
      )}
    </div>
  );
};

export default ChartCard;
